/**
 * Session Commands — intent-based RPC layer
 *
 * Each exported function represents one named host action. The function:
 *   1. Generates a stable client-side command_id (UUID) for idempotency
 *   2. Calls the appropriate Postgres RPC (which validates + locks + mutates atomically)
 *   3. Returns a structured result: { ok: true, ...payload } | { ok: false, code, message }
 *
 * Callers (hooks / page handlers) should:
 *   - Apply optimistic local state BEFORE calling the command
 *   - On { ok: false }, roll back the optimistic state and show a toast
 *   - On { ok: true }, update local version/position from the returned payload
 *
 * This layer intentionally has NO React state or side-effects — it is pure async I/O.
 *
 * Action types logged to session_host_actions:
 *   JOIN_WAITLIST, LEAVE_WAITLIST, REORDER_WAITLIST,
 *   ASSIGN_COURT, CLEAR_COURT, SET_NEXT_UP_SLOT
 *
 * Idempotency (Phase 4):
 *   Waitlist commands pass p_command_id to the RPC. The server inserts the UUID into
 *   session_host_actions (UNIQUE) on success; duplicate calls with the same commandId
 *   are returned immediately as ok without re-applying the mutation.
 *   Court mutations rely on OCC (expected_version) for natural idempotency.
 */

import {
  addPlayerToWaitlistAtomic,
  removePlayerFromWaitlistAtomic,
  reorderWaitlistAtomic,
  updateSingleCourtAtomic,
  CourtConflictError,
  ScoreSubmissionClaimError,
  claimSessionCourtScoreSubmission,
} from './sessionStateService';
import { supabase } from '../utils/supabase';

/**
 * Extract a useful message from supabase.functions.invoke errors.
 * Network/gateway failures often surface as FunctionsFetchError with a generic CORS message.
 */
async function edgeFunctionErrorMessage(error, fallback = 'Edge function request failed') {
  if (!error) return fallback;

  let message = error.message || fallback;

  try {
    const ctx = error.context;
    if (ctx && typeof ctx.json === 'function') {
      const body = await ctx.json();
      if (body?.message) message = body.message;
    } else if (ctx instanceof Response) {
      const text = await ctx.text();
      if (text) {
        try {
          const parsed = JSON.parse(text);
          message = parsed.message ?? message;
        } catch {
          message = text.slice(0, 200);
        }
      }
    }
  } catch {
    // keep message
  }

  if (message.includes('Failed to send a request to the Edge Function')) {
    return 'Could not reach the score server (network timeout or server error). Try again in a moment.';
  }

  return message;
}

const COMPUTE_RESOURCE_HOST_MESSAGE =
  'Matchmaking timed out with a full queue. Try Create Game again, or add a few players onto courts by hand to shrink the queue.';

function isComputeResourceLimitError(error, message) {
  const status = error?.context?.status;
  if (status === 546) return true;
  const text = String(message || error?.message || '');
  return /not having enough compute resources/i.test(text)
    || /WORKER_RESOURCE_LIMIT/i.test(text);
}

function hostMessageForPlanCourtFill(error, message) {
  if (isComputeResourceLimitError(error, message)) {
    return COMPUTE_RESOURCE_HOST_MESSAGE;
  }
  return message;
}

/** Generate a RFC-4122 v4 UUID for command idempotency keys. */
function generateCommandId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// ============================================================================
// WAITING LIST COMMANDS
// ============================================================================

/**
 * Add one player to the end of the waiting list.
 * Safe to call concurrently — the Postgres RPC uses MAX(position)+1.
 * Idempotent: a stable commandId is generated per call; duplicate network retries
 * with the same commandId are no-ops on the server.
 *
 * @param {string|null} tabInstanceId – writer lease tab ID (legacy; no-op after Phase 3)
 * @returns {{ ok: boolean, position?: number, commandId?: string, code?: string, message?: string }}
 */
export async function joinWaitlistCmd(sessionId, playerId, tabInstanceId = null) {
  const commandId = generateCommandId();
  try {
    const position = await addPlayerToWaitlistAtomic(sessionId, playerId, tabInstanceId, commandId);
    return { ok: true, position, commandId };
  } catch (err) {
    console.error('[SessionCmd] joinWaitlist failed:', err);
    return { ok: false, code: 'WAITLIST_JOIN_FAILED', message: err.message };
  }
}

/**
 * Remove one player from the waiting list.
 * Idempotent by design: if the player is already off the waitlist the UPDATE is a no-op.
 * The server-side command_id additionally prevents double-application of the audit log.
 *
 * @param {string|null} tabInstanceId – writer lease tab ID (legacy; no-op after Phase 3)
 * @returns {{ ok: boolean, commandId?: string, code?: string, message?: string }}
 */
export async function leaveWaitlistCmd(sessionId, playerId, tabInstanceId = null) {
  const commandId = generateCommandId();
  try {
    await removePlayerFromWaitlistAtomic(sessionId, playerId, tabInstanceId, commandId);
    return { ok: true, commandId };
  } catch (err) {
    console.error('[SessionCmd] leaveWaitlist failed:', err);
    return { ok: false, code: 'WAITLIST_LEAVE_FAILED', message: err.message };
  }
}

/**
 * Rewrite all waiting list positions after a drag-to-reorder gesture.
 * The RPC runs all position updates in one transaction.
 * Idempotent: duplicate calls with the same commandId are no-ops on the server.
 *
 * @param {string[]}    orderedPlayerIds – player IDs in desired display order
 * @param {string|null} tabInstanceId    – writer lease tab ID (legacy; no-op after Phase 3)
 * @returns {{ ok: boolean, commandId?: string, code?: string, message?: string }}
 */
export async function reorderWaitlistCmd(sessionId, orderedPlayerIds, tabInstanceId = null) {
  const commandId = generateCommandId();
  try {
    await reorderWaitlistAtomic(sessionId, orderedPlayerIds, tabInstanceId, commandId);
    return { ok: true, commandId };
  } catch (err) {
    console.error('[SessionCmd] reorderWaitlist failed:', err);
    return { ok: false, code: 'WAITLIST_REORDER_FAILED', message: err.message };
  }
}

// ============================================================================
// COURT ASSIGNMENT COMMANDS
// ============================================================================

/**
 * Assign players to an active or next-up court slot.
 * Uses update_court_state_atomic (SELECT FOR UPDATE + OCC version check).
 *
 * Pass expectedVersion = -1 to skip the OCC check (force write).
 *
 * @param {string|null} tabInstanceId – writer lease tab ID; passed to the RPC for server-side lease check
 * @returns {{ ok: boolean, newVersion?: number, code?: string, message?: string }}
 */
export async function assignPlayersToCourtCmd(
  sessionId,
  courtNumber,
  courtType,
  expectedVersion,
  courtData,
  tabInstanceId = null
) {
  try {
    const result = await updateSingleCourtAtomic(
      sessionId,
      courtNumber,
      courtType,
      expectedVersion,
      courtData,
      tabInstanceId
    );
    return { ok: true, newVersion: result.newVersion };
  } catch (err) {
    if (err instanceof CourtConflictError) {
      return {
        ok: false,
        code: 'COURT_CONFLICT',
        message: 'Another host modified this court first. Refreshing…',
        currentVersion: err.currentVersion,
      };
    }
    console.error('[SessionCmd] assignPlayersToCourt failed:', err);
    return { ok: false, code: 'COURT_ASSIGN_FAILED', message: err.message };
  }
}

/**
 * Clear all players from an active court slot (e.g. after a game).
 * Thin wrapper around assignPlayersToCourtCmd with empty teams.
 *
 * @returns {{ ok: boolean, newVersion?: number, code?: string, message?: string }}
 */
export async function clearCourtCmd(sessionId, courtNumber, courtType, expectedVersion) {
  return assignPlayersToCourtCmd(sessionId, courtNumber, courtType, expectedVersion, {
    team1: [],
    team2: [],
    inProgress: false,
    gameCreation: null,
  });
}

// ============================================================================
// MATCH FINALISATION COMMAND
// ============================================================================

/**
 * True when this upcoming slot roster was persisted to DB (OCC acked).
 * Local-only matchmaking previews use version -1 and must not override server refill on score.
 */
/**
 * Atomically record a completed match and clear the court in one Postgres txn.
 *
 * This replaces the three-step client sequence:
 *   claim → createGame (JS) → finalize
 *
 * ELO updates are NOT performed here — the caller should apply ELO separately
 * using the returned `gameId` as an idempotent key.
 *
 * The claim must already be held (call claimSessionCourtScoreSubmission first).
 *
 * @param {string} sessionId
 * @param {number} courtNumber
 * @param {object} matchData
 *   - clubId, seasonId
 *   - team1PlayerIds, team2PlayerIds  (UUID strings)
 *   - team1Score, team2Score
 *   - winningTeam  (1 | 2)
 *   - gameTypeId
 *   - startTime, endTime  (ISO strings)
 *   - gameCreateMethod  (game_create_method_enum value)
 *   - matchmakingAlgorithm  (optional)
 *   - scheduledGameId  (optional UUID — passed when origin is scheduled; validated in RPC)
 *   - team1PairType, team2PairType  ('levels' | 'mixed' | 'singles')
 * @returns {{ ok: boolean, gameId?: string, courtVersion?: number, promoted?: boolean, promotedFromSlot?: number|null, code?: string, message?: string }}
 */
export async function finishMatchCmd(sessionId, courtNumber, matchData) {
  const {
    clubId,
    seasonId = null,
    team1PlayerIds = [],
    team2PlayerIds = [],
    team1Score,
    team2Score,
    winningTeam,
    gameTypeId = null,
    startTime = null,
    endTime = new Date().toISOString(),
    gameCreateMethod = null,
    matchmakingAlgorithm = null,
    scheduledGameId = null,
    team1PairType = 'levels',
    team2PairType = 'levels',
    tabInstanceId = null,  // writer lease tab ID for server-side lease check
    refillIntent = null,   // { fillEmptySlots: true } — server fills empty next_up shells post-promotion
  } = matchData;

  try {
    const { data, error } = await supabase.rpc('finish_session_match', {
      p_session_id:             sessionId,
      p_court_number:           courtNumber,
      p_club_id:                clubId,
      p_season_id:              seasonId,
      p_team1_player_ids:       team1PlayerIds.filter(Boolean),
      p_team2_player_ids:       team2PlayerIds.filter(Boolean),
      p_team1_score:            team1Score,
      p_team2_score:            team2Score,
      p_winning_team:           winningTeam,
      p_game_type_id:           gameTypeId,
      p_start_time:             startTime,
      p_end_time:               endTime,
      p_game_create_method:     gameCreateMethod,
      p_matchmaking_algorithm:  matchmakingAlgorithm,
      p_team1_pair_type:        team1PairType,
      p_team2_pair_type:        team2PairType,
      p_scheduled_game_id:      scheduledGameId || null,
      p_tab_instance_id:        tabInstanceId || null,
      // p_next_up_snapshot removed (Phase 4): DB is source of truth after quiescence gate
      p_refill_intent:          refillIntent || null,
    });

    if (error) {
      console.error('[SessionCmd] finishMatch RPC error:', error);
      return { ok: false, code: 'FINISH_MATCH_ERROR', message: error.message };
    }

    if (data?.ok === false) {
      return { ok: false, code: data.code, message: data.message };
    }

    return { ok: true, gameId: data.game_id, courtVersion: data.court_version,
      promoted: data.promoted === true,
      promotedFromSlot: data.promoted_from_slot ?? null,
    };
  } catch (err) {
    console.error('[SessionCmd] finishMatch unexpected error:', err);
    return { ok: false, code: 'FINISH_MATCH_ERROR', message: err.message };
  }
}

/**
 * Peg Board picker/host confirm: place four waitlist players on the first empty court or next_up slot.
 */
export async function selectPegBoardGameCmd({
  sessionId,
  expectedPickerId,
  partnerId,
  opp1Id,
  opp2Id,
  algorithm = 'peg_board_manual',
}) {
  try {
    const { data, error } = await supabase.rpc('select_peg_board_game', {
      p_session_id: sessionId,
      p_expected_picker_id: expectedPickerId,
      p_partner_id: partnerId,
      p_opp1_id: opp1Id,
      p_opp2_id: opp2Id,
      p_algorithm: algorithm,
    });
    if (error) {
      console.error('[SessionCmd] selectPegBoardGame RPC error:', error);
      return { ok: false, code: 'SELECT_PEG_BOARD_ERROR', message: error.message };
    }
    if (data?.ok === false) {
      return { ok: false, code: data.code, message: data.message };
    }
    return {
      ok: true,
      courtType: data.court_type,
      courtNumber: data.court_number,
      courtVersion: data.court_version,
      algorithm: data.algorithm,
    };
  } catch (err) {
    console.error('[SessionCmd] selectPegBoardGame unexpected error:', err);
    return { ok: false, code: 'SELECT_PEG_BOARD_ERROR', message: err.message };
  }
}

/**
 * Peg Board host score: writes the game, returns players in queue-return order, promotes upcoming only.
 */
export async function finishPegBoardMatchCmd(sessionId, courtNumber, matchData) {
  const {
    clubId,
    seasonId = null,
    team1PlayerIds = [],
    team2PlayerIds = [],
    team1Score,
    team2Score,
    winningTeam,
    gameTypeId = null,
    startTime = null,
    endTime = new Date().toISOString(),
    matchmakingAlgorithm = 'peg_board_manual',
    team1PairType = 'levels',
    team2PairType = 'levels',
    tabInstanceId = null,
  } = matchData;

  try {
    const { data, error } = await supabase.rpc('finish_peg_board_match', {
      p_session_id: sessionId,
      p_court_number: courtNumber,
      p_club_id: clubId,
      p_season_id: seasonId,
      p_team1_player_ids: team1PlayerIds.filter(Boolean),
      p_team2_player_ids: team2PlayerIds.filter(Boolean),
      p_team1_score: team1Score,
      p_team2_score: team2Score,
      p_winning_team: winningTeam,
      p_game_type_id: gameTypeId,
      p_start_time: startTime,
      p_end_time: endTime,
      p_matchmaking_algorithm: matchmakingAlgorithm,
      p_team1_pair_type: team1PairType,
      p_team2_pair_type: team2PairType,
      p_tab_instance_id: tabInstanceId || null,
    });

    if (error) {
      console.error('[SessionCmd] finishPegBoardMatch RPC error:', error);
      return { ok: false, code: 'FINISH_PEG_BOARD_ERROR', message: error.message };
    }
    if (data?.ok === false) {
      return { ok: false, code: data.code, message: data.message };
    }
    return {
      ok: true,
      gameId: data.game_id,
      courtVersion: data.court_version,
      promoted: data.promoted === true,
      promotedFromSlot: data.promoted_from_slot ?? null,
    };
  } catch (err) {
    console.error('[SessionCmd] finishPegBoardMatch unexpected error:', err);
    return { ok: false, code: 'FINISH_PEG_BOARD_ERROR', message: err.message };
  }
}

/**
 * Submit a completed match score via the complete-session-score Edge Function.
 *
 * This is the PR4+ server-authoritative score path.  The EF runs:
 *   prepare_score_transition → pure matchmaking compute → commit_score_transition
 * in one round-trip.  The caller must NOT also call finishMatchCmd — the EF writes
 * the game and applies server fills atomically.
 *
 * @param {object} params - Score and session parameters
 * @param {number|null} [params.expectedCourtVersion] - OCC version for inline claim in prepare_score_transition
 * @returns {{ ok: boolean, gameId?: string, courtVersion?: number, promoted?: boolean, promotedFromSlot?: number|null, fills?: object[], eloDeltas?: object, code?: string, message?: string, retryable?: boolean }}
 */
export async function completeSessionScoreCmd({
  sessionId,
  courtNumber,
  tabInstanceId = null,
  team1PlayerIds = [],
  team2PlayerIds = [],
  team1Score,
  team2Score,
  winningTeam = null,
  gameTypeId = null,
  startTime = null,
  endTime = new Date().toISOString(),
  scoreEventId,
  gameCreateMethod = null,
  matchmakingAlgorithm = null,
  scheduledGameId = null,
  eloUpdates = null,
  expectedCourtVersion = null,
}) {
  try {
    const { data, error } = await supabase.functions.invoke('complete-session-score', {
      body: {
        sessionId,
        courtNumber,
        tabInstanceId:   tabInstanceId || null,
        team1PlayerIds:  team1PlayerIds.filter(Boolean),
        team2PlayerIds:  team2PlayerIds.filter(Boolean),
        team1Score:      Number(team1Score) || 0,
        team2Score:      Number(team2Score) || 0,
        winningTeam:     winningTeam ?? null,
        gameTypeId:      gameTypeId || null,
        startTime:       startTime || null,
        endTime,
        scoreEventId,
        gameCreateMethod:     gameCreateMethod || null,
        matchmakingAlgorithm: matchmakingAlgorithm || null,
        scheduledGameId:      scheduledGameId || null,
        eloUpdates:           Array.isArray(eloUpdates) && eloUpdates.length > 0 ? eloUpdates : null,
        expectedCourtVersion: expectedCourtVersion ?? null,
      },
    });

    if (error) {
      console.error('[SessionCmd] completeSessionScore EF error:', error);
      return {
        ok: false,
        code: 'EF_INVOKE_ERROR',
        message: await edgeFunctionErrorMessage(error),
        retryable: true,
      };
    }

    if (!data?.ok) {
      return {
        ok:        false,
        code:      data?.code    ?? 'EF_ERROR',
        message:   data?.message ?? 'complete-session-score failed',
        retryable: data?.retryable ?? false,
        currentVersion: data?.currentVersion ?? data?.current_version ?? null,
      };
    }

    return {
      ok:               true,
      gameId:           data.gameId,
      courtVersion:     data.courtVersion,
      promoted:         data.promoted === true,
      promotedFromSlot: data.promotedFromSlot ?? null,
      fills:            data.fills ?? [],
      eloDeltas:        data.eloDeltas ?? null,
    };
  } catch (err) {
    console.error('[SessionCmd] completeSessionScore unexpected error:', err);
    return { ok: false, code: 'EF_INVOKE_ERROR', message: err.message };
  }
}

/**
 * Plan and apply matchmaking for a single upcoming court slot via the
 * plan-upcoming-slot Edge Function (PR5+, server-authoritative path).
 *
 * Calls materialise_upcoming_slot → pure compute → apply_upcoming_matchmaking
 * in one round-trip.  Returns the computed fills and the new court version.
 *
 * @param {object} params
 * @returns {{ ok: boolean, fills?: object[], newVersion?: number, slotIndex?: number, code?: string, message?: string, retryable?: boolean }}
 */
export async function planUpcomingSlotCmd({ sessionId, slotIndex, tabInstanceId = null }) {
  return planCourtFillCmd({
    sessionId,
    targetKind: 'next_up',
    targetIndex: slotIndex,
    tabInstanceId,
  });
}

/**
 * Plan and apply matchmaking for an upcoming slot or active court via plan-court-fill EF.
 *
 * @param {object} params
 * @returns {{ ok: boolean, fills?: object[], newVersion?: number, targetKind?: string, targetIndex?: number, slotIndex?: number, code?: string, message?: string, retryable?: boolean }}
 */
export async function planCourtFillCmd({
  sessionId,
  targetKind,
  targetIndex,
  targetIndexes = null,
  dryRun = false,
  tabInstanceId = null,
}) {
  try {
    const { data, error } = await supabase.functions.invoke('plan-court-fill', {
      body: {
        sessionId,
        targetKind,
        targetIndex,
        targetIndexes: targetIndexes?.length ? targetIndexes : undefined,
        dryRun: dryRun === true,
        tabInstanceId: tabInstanceId || null,
      },
    });

    if (error) {
      console.error('[SessionCmd] planCourtFill EF error:', error);
      const rawMessage = await edgeFunctionErrorMessage(error, 'Server matchmaking request failed');
      return {
        ok: false,
        code: 'EF_INVOKE_ERROR',
        message: hostMessageForPlanCourtFill(error, rawMessage),
        retryable: true,
      };
    }

    if (!data?.ok) {
      return {
        ok:        false,
        code:      data?.code    ?? 'EF_ERROR',
        message:   data?.message ?? 'plan-court-fill failed',
        retryable: data?.retryable ?? false,
      };
    }

    return {
      ok:          true,
      fills:       data.fills ?? [],
      debugInfo:   data.debugInfo ?? null,
      stateVersion: data.stateVersion ?? null,
      newVersion:  data.newVersion ?? null,
      targetKind:  data.targetKind ?? targetKind,
      targetIndex: data.targetIndex ?? targetIndex,
      slotIndex:   data.slotIndex ?? (targetKind === 'next_up' ? targetIndex : undefined),
      dryRun:      data.dryRun === true,
    };
  } catch (err) {
    console.error('[SessionCmd] planCourtFill unexpected error:', err);
    return {
      ok: false,
      code: 'EF_INVOKE_ERROR',
      message: hostMessageForPlanCourtFill(err, err.message),
    };
  }
}

/**
 * Update one slot in the next-up queue atomically.
 * Equivalent to assignPlayersToCourtCmd with courtType = 'next_up'.
 *
 * @param {string|null} tabInstanceId – writer lease tab ID; passed to the RPC for server-side lease check
 * @returns {{ ok: boolean, newVersion?: number, code?: string, message?: string }}
 */
export async function setNextUpSlotCmd(
  sessionId,
  slotIndex,
  expectedVersion,
  slotData,
  tabInstanceId = null
) {
  const result = await assignPlayersToCourtCmd(
    sessionId,
    slotIndex,
    'next_up',
    expectedVersion,
    slotData,
    tabInstanceId
  );
  if (!result.ok) result.code = result.code === 'COURT_ASSIGN_FAILED' ? 'SET_NEXT_UP_FAILED' : result.code;
  return result;
}

/**
 * Correct a completed competitive game score (live session, last-game gate).
 * ELO math is computed by the caller; this wrapper only persists via RPC.
 *
 * @returns {{ ok: boolean, eloDeltas?: object, previousScores?: object, winningTeam?: number, code?: string, message?: string }}
 */
export async function correctSessionGameScoreCmd({
  sessionGameId,
  team1Score,
  team2Score,
  eloUpdates = [],
  commandId = null,
}) {
  const resolvedCommandId = commandId || generateCommandId();
  try {
    const { data, error } = await supabase.rpc('correct_session_game_score', {
      p_session_game_id: sessionGameId,
      p_team1_score: Number(team1Score),
      p_team2_score: Number(team2Score),
      p_elo_updates: Array.isArray(eloUpdates) ? eloUpdates : [],
      p_command_id: resolvedCommandId,
    });

    if (error) {
      console.error('[SessionCmd] correctSessionGameScore RPC error:', error);
      return { ok: false, code: 'CORRECT_SCORE_ERROR', message: error.message };
    }

    if (!data?.ok) {
      return {
        ok: false,
        code: data?.code ?? 'CORRECT_SCORE_ERROR',
        message: data?.message ?? 'Could not update the score.',
      };
    }

    return {
      ok: true,
      eloDeltas: data.elo_deltas ?? {},
      previousScores: data.previous_scores ?? null,
      winningTeam: data.winning_team ?? null,
      commandId: resolvedCommandId,
    };
  } catch (err) {
    console.error('[SessionCmd] correctSessionGameScore unexpected error:', err);
    return { ok: false, code: 'CORRECT_SCORE_ERROR', message: err.message };
  }
}

/**
 * Correct a completed competitive game after End Session (club-latest + window).
 * ELO math + in-session chain replay are computed by the caller.
 */
export async function correctSessionGameScorePostSessionCmd({
  sessionGameId,
  team1Score,
  team2Score,
  eloUpdates = [],
  chainUpdates = [],
  commandId = null,
}) {
  const resolvedCommandId = commandId || generateCommandId();
  try {
    const { data, error } = await supabase.rpc('correct_session_game_score_post_session', {
      p_session_game_id: sessionGameId,
      p_team1_score: Number(team1Score),
      p_team2_score: Number(team2Score),
      p_elo_updates: Array.isArray(eloUpdates) ? eloUpdates : [],
      p_chain_updates: Array.isArray(chainUpdates) ? chainUpdates : [],
      p_command_id: resolvedCommandId,
    });

    if (error) {
      console.error('[SessionCmd] correctSessionGameScorePostSession RPC error:', error);
      return { ok: false, code: 'CORRECT_SCORE_ERROR', message: error.message };
    }

    if (!data?.ok) {
      return {
        ok: false,
        code: data?.code ?? 'CORRECT_SCORE_ERROR',
        message:
          data?.code === 'STALE_CHAIN'
            ? 'A game finished while you were editing. Refresh and try again.'
            : (data?.message ?? 'Could not update the score.'),
      };
    }

    return {
      ok: true,
      correctionMode: data.correction_mode === 'live' ? 'live' : 'post_session',
      gamesReplayed: data.games_replayed ?? 0,
      previousScores: data.previous_scores ?? null,
      winningTeam: data.winning_team ?? null,
      commandId: resolvedCommandId,
    };
  } catch (err) {
    console.error('[SessionCmd] correctSessionGameScorePostSession unexpected error:', err);
    return { ok: false, code: 'CORRECT_SCORE_ERROR', message: err.message };
  }
}
